import {
  SET_ALERTS,
  SET_DELIVERY_CONFIGS,
  SET_SHOW_ADD_DELIVERY_CONFIG,
  SET_SMTP_CONFIG,
  SET_SMTP_CONFIG_IS_DEFAULT,
} from "@/store/mutation-types";
import CacheService from "@/services/CacheService";
import { RequestMethod } from "@/types/request-method";
import { AlertsState, Alert, DeliveryConfig, SMTPConfig, SMTPConfigResponse, SMTPTestResponse } from "@/types/alert";
import { RootState } from "@/types/root-state";
import * as Msg from "@/config/notification";
import { setNotificationSuccess, setNotificationError } from "@/utils/notification";
import { isAdminUsingProd } from "@/utils/admin";
import { Module, MutationTree, ActionTree, GetterTree } from "vuex";

const namespaced = true;

const state: AlertsState = {
  alerts: null,
  deliveryConfigs: null,
  showAddDeliveryConfig: false,
  SMTPConfig: false,
  SMTPConfigIsDefault: true, // Kapacitor defaults used initially
};

const mutations: MutationTree<AlertsState> = {
  [SET_ALERTS]: (state, alerts: Alert[]) => {
    state.alerts = alerts;
  },
  [SET_DELIVERY_CONFIGS]: (state, deliveryConfigs: DeliveryConfig[]) => {
    state.deliveryConfigs = deliveryConfigs;
  },
  [SET_SHOW_ADD_DELIVERY_CONFIG]: (state, show: boolean) => {
    state.showAddDeliveryConfig = show;
  },
  [SET_SMTP_CONFIG]: (state, SMTPConfig: SMTPConfig | null) => {
    state.SMTPConfig = SMTPConfig;
  },
  [SET_SMTP_CONFIG_IS_DEFAULT]: (state, isDefault: boolean) => {
    state.SMTPConfigIsDefault = isDefault;
  },
};

const actions: ActionTree<AlertsState, RootState> = {
  fetch({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/alerts`, RequestMethod.GET)
      .then(response => {
        const alerts: Alert[] = response.data;
        commit(SET_ALERTS, alerts);
      })
      .catch(error => {
        // Set from null to [] to know fetching has completed
        // to differentiate between loading and no data
        commit(SET_ALERTS, []);
        setNotificationError(dispatch, `${Msg.ALERTS_ERROR} (${error})`);
      });
  },
  add({ dispatch, rootState }, alert: Alert) {
    if (isAdminUsingProd()) return;
    const data = {
      trigger_type: alert.trigger_type,
      trigger_threshold: alert.trigger_threshold,
      delivery_config_id: alert.delivery_config_id,
      description: alert.description,
      enabled: true,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/alerts`, RequestMethod.POST, {}, data)
      .then(() => {
        dispatch("fetch");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.ALERT_ADD_ERROR} (${error})`);
      });
  },
  update({ dispatch, rootState }, alert: Alert) {
    if (isAdminUsingProd()) return;
    const data = {
      id: alert.id,
      trigger_type: alert.trigger_type,
      trigger_threshold: alert.trigger_threshold,
      delivery_config_id: alert.delivery_config_id,
      description: alert.description,
      enabled: alert.enabled,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/alerts/${alert.id}`, RequestMethod.PUT, {}, data)
      .then()
      .catch(error => {
        setNotificationError(dispatch, `${Msg.ALERT_UPDATE_ERROR} (${error})`);
      });
  },
  remove({ dispatch, rootState }, alert: Alert) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/alerts/${alert.id}`, RequestMethod.DELETE)
      .then(() => {
        dispatch("fetch");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.ALERT_REMOVE_ERROR} (${error})`);
      });
  },
  fetchDeliveryConfigs({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/alerts/configs`, RequestMethod.GET)
      .then(response => {
        const deliveryConfigs: DeliveryConfig[] = response.data;
        commit(SET_DELIVERY_CONFIGS, deliveryConfigs);
      })
      .catch(error => {
        // Set from null to [] to know fetching has completed
        // to differentiate between loading and no data
        commit(SET_DELIVERY_CONFIGS, []);
        setNotificationError(dispatch, `${Msg.ALERTS_CONFIG_ERROR} (${error})`);
      });
  },
  addDeliveryConfig({ dispatch, rootState }, deliveryConfig: DeliveryConfig) {
    if (isAdminUsingProd()) return;

    // make sure first Slack channel character is #
    if (
      deliveryConfig.delivery_type === "slack" &&
      deliveryConfig.slack_channel &&
      !deliveryConfig.slack_channel.startsWith("#")
    ) {
      deliveryConfig.slack_channel = `#${deliveryConfig.slack_channel}`;
    }

    const data = {
      delivery_type: deliveryConfig.delivery_type,
      email: deliveryConfig.email,
      slack_url: deliveryConfig.slack_url,
      slack_channel: deliveryConfig.slack_channel,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/alerts/configs`, RequestMethod.POST, {}, data)
      .then(() => {
        dispatch("fetchDeliveryConfigs");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.ALERT_CONFIG_ADD_ERROR} (${error})`);
      });
  },
  removeDeliveryConfig({ dispatch, rootState }, deliveryConfigID: number) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/alerts/configs/${deliveryConfigID}`, RequestMethod.DELETE)
      .then(() => {
        dispatch("fetchDeliveryConfigs");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.ALERT_CONFIG_REMOVE_ERROR} (${error})`);
      });
  },
  setShowAddDeliveryConfig({ commit }, show: boolean) {
    commit(SET_SHOW_ADD_DELIVERY_CONFIG, show);
  },
  fetchSMTPConfig({ commit, dispatch }) {
    return CacheService.request(`smtp`, RequestMethod.GET)
      .then(response => {
        const configResponse: SMTPConfigResponse = response.data;
        const opts = configResponse.elements[0].options;

        // Determine Kapacitor defaults are being used if username is an empty string.

        const config: SMTPConfig = {
          from: opts.from,
          username: opts.username,
          password: "",
          host: opts.username ? opts.host : "", // Do not use Kapacitor `localhost` default
          port: opts.port, // Kapacitor default is 25
        };

        commit(SET_SMTP_CONFIG, config);

        if (opts.username !== "") {
          commit(SET_SMTP_CONFIG_IS_DEFAULT, false);
        }
      })
      .catch(error => {
        commit(SET_SMTP_CONFIG, null);
        commit(SET_SMTP_CONFIG_IS_DEFAULT, true);
        setNotificationError(dispatch, `${Msg.SMTP_CONFIG_FETCH_ERROR} (${error})`);
      });
  },
  setSMTPConfig({ commit, dispatch }, config: SMTPConfig) {
    if (isAdminUsingProd()) return;

    return CacheService.request(`smtp`, RequestMethod.POST, {}, config)
      .then(response => {
        const configResponse: SMTPConfigResponse = response.data;
        const opts = configResponse.elements[0].options;

        const config: SMTPConfig = {
          from: opts.from,
          username: opts.username,
          password: "",
          host: opts.host,
          port: opts.port,
        };

        commit(SET_SMTP_CONFIG, config);

        if (opts.username !== "") {
          commit(SET_SMTP_CONFIG_IS_DEFAULT, false);
        }
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.SMTP_CONFIG_SET_ERROR} (${error})`);
      });
  },
  sendTestEmail({ dispatch }, email: string) {
    if (isAdminUsingProd()) return;

    const testOpts = {
      to: email,
      subject: "[CacheSight Alert] Test email alert",
      body: "This is test email alert. Your SMTP settings are configured correctly!",
    };

    return CacheService.request(`smtp/test`, RequestMethod.POST, {}, testOpts)
      .then(response => {
        const testResponse: SMTPTestResponse = response.data;

        if (testResponse.success) {
          setNotificationSuccess(dispatch, Msg.TEST_EMAIL_SUCCESS);
        } else {
          setNotificationError(dispatch, `${Msg.TEST_EMAIL_ERROR} (${testResponse.message})`);
        }
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.TEST_EMAIL_ERROR} (${error})`);
      });
  },
};

const getters: GetterTree<AlertsState, RootState> = {
  deliveryConfigInUse: state => (deliveryConfigID: number) => {
    return state.alerts?.find(alert => alert.delivery_config_id === deliveryConfigID);
  },
  deliveryConfigByID: state => (deliveryConfigID: number | null) => {
    return state.deliveryConfigs?.find(deliveryConfig => deliveryConfig.id === deliveryConfigID);
  },
  isSMTPConfigSet: (state, getters, rootState, rootGetters) => {
    const features = rootGetters["settings/activeCacheFeatures"];
    if (features["alert-config"] === 0) {
      return true;
    }
    return !state.SMTPConfigIsDefault;
  },
};

export const alerts: Module<AlertsState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
